import React from 'react';
import { Avatar, Button, Flex, Icon, TabPanel, TabsLayout, Text } from '@televet/kibble-ui';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { CurrentRewards } from './CurrentRewards';
import { RewardHistory } from './RewardHistory';
import { useGetPetParentLoyaltyProgramDataQuery } from 'shared/types/graphql';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import { LoyaltyEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useTracking } from 'shared/providers/TrackingProvider';

export enum RewardsTab {
  CurrentRewards, // 0
  RewardsHistory, // 1
}

export const RewardsPanel = (): JSX.Element => {
  const { currentClinicPetParentId, viewClinicPetParent, selectedLoyaltyRewardsTab } = useSidePanelSearch();
  const { trackEvent } = useTracking();

  const { data: petParentData } = useGetPetParentLoyaltyProgramDataQuery({
    variables: {
      where: {
        id: {
          equals: currentClinicPetParentId,
        },
      },
    },
    skip: !currentClinicPetParentId,
  });

  const petParent = petParentData?.findFirstClinicPetParent;
  const petParentName = `${petParent?.firstName} ${petParent?.lastName}`;

  return (
    <Flex flexDirection="column" className="RewardsPanel" overflow="hidden">
      {!!petParent ? (
        <>
          <Flex alignItems="center" pt={5} pb={7} px={2} className="RewardsPanel__Header">
            <Button
              variant="ghostNeutral"
              leftIcon={<Icon name="chevronLeft" />}
              size="xs"
              p={0}
              onClick={(): void => viewClinicPetParent({ clinicPetParentId: currentClinicPetParentId || '' })}
            />
            <Flex alignItems="center" ml={2}>
              <Avatar name={petParentName} size="xs" />
              <Text ml={2} fontWeight="bold" isTruncated size="xl">
                {petParentName}
              </Text>
            </Flex>
          </Flex>
          {petParent.loyaltyAccountHolder && (
            <Flex px={2} className="RewardsPanel__Tabs" overflow="hidden">
              <TabsLayout
                onChange={(index): void => {
                  trackEvent(
                    index === 0 ? LoyaltyEvents.SidepanelCurrentRewards : LoyaltyEvents.SidepanelRewardsHistory,
                    {
                      source: 'tab',
                    },
                  );
                }}
                tabList={[{ label: 'Current Rewards' }, { label: 'Rewards History' }]}
                tabPanelsStyle={{
                  mt: 3,
                  overflowY: 'auto',
                }}
                isLazy
                tabPanels={[
                  <TabPanel key={0}>
                    <CurrentRewards
                      accountHolderId={petParent.loyaltyAccountHolder.id}
                      programId={petParent.loyaltyAccountHolder.programId}
                    />
                  </TabPanel>,
                  <TabPanel key={1}>
                    <RewardHistory accountId={petParent.loyaltyAccountHolder.account.id} />
                  </TabPanel>,
                ]}
                size="sm"
                isFitted
                defaultIndex={selectedLoyaltyRewardsTab}
                display="flex"
                flexDirection="column"
                overflow="hidden"
                w="100%"
              />
            </Flex>
          )}
        </>
      ) : (
        <LoadingIndicator />
      )}
    </Flex>
  );
};
